import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

interface VoiceContextType {
  isVoiceMuted: boolean;
  toggleVoiceMute: () => void;
  setVoiceMuted: (muted: boolean) => void;
}

const VoiceContext = createContext<VoiceContextType | undefined>(undefined);

const VOICE_MUTE_STORAGE_KEY = 'haven_voice_muted';

// Helper function to mute/unmute all audio and video elements on the page (exported for intro to re-apply on mute)
export const muteAllAudioElements = (muted: boolean) => {
  const audioElements = document.querySelectorAll('audio, video');
  audioElements.forEach((element) => {
    (element as HTMLMediaElement).muted = muted;
  });
};

export const VoiceMuteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize from localStorage, defaulting to unmuted (false)
  const [isVoiceMuted, setIsVoiceMuted] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(VOICE_MUTE_STORAGE_KEY);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Use ref to track if initial mute has been applied
  const hasAppliedInitialMute = useRef(false);

  // Persist to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem(VOICE_MUTE_STORAGE_KEY, String(isVoiceMuted));
    } catch {
      // Ignore storage errors
    }
  }, [isVoiceMuted]);

  // Apply mute state to all audio elements when it changes
  useEffect(() => {
    muteAllAudioElements(isVoiceMuted);
    hasAppliedInitialMute.current = true;

    // Set up a MutationObserver to handle dynamically added audio elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            // Check if the added node is an audio/video element
            if (element.tagName === 'AUDIO' || element.tagName === 'VIDEO') {
              (element as HTMLMediaElement).muted = isVoiceMuted;
            }
            // Also check for audio/video elements within the added node
            const mediaElements = element.querySelectorAll('audio, video');
            mediaElements.forEach((media) => {
              (media as HTMLMediaElement).muted = isVoiceMuted;
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isVoiceMuted]);

  const toggleVoiceMute = useCallback(() => {
    setIsVoiceMuted((prev) => !prev);
  }, []);

  const setVoiceMuted = useCallback((muted: boolean) => {
    muteAllAudioElements(muted); // Apply immediately so intro mute works without waiting for effect
    setIsVoiceMuted(muted);
  }, []);

  return (
    <VoiceContext.Provider value={{ isVoiceMuted, toggleVoiceMute, setVoiceMuted }}>
      {children}
    </VoiceContext.Provider>
  );
};

export const useVoiceMuteContext = (): VoiceContextType => {
  const context = useContext(VoiceContext);
  if (context === undefined) {
    throw new Error('useVoiceMuteContext must be used within a VoiceMuteProvider');
  }
  return context;
};

export default VoiceContext;
